/**
 * 获取图片缩放比例
 * @param {Object} params - 参数对象
 * @param {Object} params.ImageBox - 容器尺寸 { w, h }
 * @param {Object} params.Image - 图片尺寸 { w, h }
 * @returns {Object} 缩放比例和计算后的尺寸
 */
import { DEFAULT_MESSAGES } from "../const/constants.js";

export const getScaleRatio = ({ ImageBox, Image }) => {
  const wRatio = ImageBox?.w / Image.w;
  const hRatio = ImageBox?.h / Image.h;
  const ratio = hRatio < wRatio ? hRatio : wRatio;
  return {
    Ratio: ratio,
    ImageWidth: Image.w * ratio,
    ImageHeight: Image.h * ratio,
  };
};

/**
 * Strip EXIF data from a JPEG image URL to prevent browser auto-rotation.
 * Returns a blob ObjectURL for JPEG, or the original URL for non-JPEG.
 * @param {string} src - Image URL or base64 data URL
 * @returns {Promise<string>} URL without EXIF orientation data
 */
export async function stripExifFromUrl(src) {
  // base64 images are already corrected by server, no stripping needed
  if (!src || src.startsWith("data:")) return src;

  try {
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    const dataView = new DataView(arrayBuffer);

    // Check if JPEG (SOI marker: 0xFFD8)
    if (dataView.getUint16(0) !== 0xffd8) {
      return src;
    }

    const segments = [];
    segments.push(new Uint8Array(arrayBuffer, 0, 2)); // SOI marker

    let offset = 2;
    while (offset < dataView.byteLength - 1) {
      if (dataView.getUint8(offset) !== 0xff) break;

      const marker = dataView.getUint16(offset);

      // SOS marker (0xFFDA): image data starts here, append rest
      if (marker === 0xffda) {
        segments.push(new Uint8Array(arrayBuffer, offset));
        break;
      }

      const segmentLength = dataView.getUint16(offset + 2) + 2;

      // Skip APP1 (0xFFE1) — this is the EXIF segment
      if (marker !== 0xffe1) {
        segments.push(new Uint8Array(arrayBuffer, offset, segmentLength));
      }

      offset += segmentLength;
    }

    const newBlob = new Blob(segments, { type: "image/jpeg" });
    return URL.createObjectURL(newBlob);
  } catch (e) {
    console.warn("Strip EXIF failed, fallback to original src:", e);
    return src;
  }
}

/**
 * Get the natural dimensions of an image.
 * @param {string} url - Image URL
 * @param {boolean} isPdf - unused
 * @param {boolean} stripExif - whether to strip EXIF before reading dimensions
 * @returns {Promise<Object>} { width, height }
 */
export function getImgNatural(url, isPdf, stripExif = false) {
  return new Promise(async (resolve, reject) => {
    if (!url) {
      resolve({ width: 0, height: 0 });
      return;
    }

    // Only strip EXIF when explicitly enabled, so naturalWidth/Height match the rendered image
    const strippedUrl = stripExif ? await stripExifFromUrl(url) : url;
    let objectUrl = strippedUrl !== url ? strippedUrl : null;

    const temImg = new Image();
    temImg.src = strippedUrl;

    temImg.onload = function () {
      resolve({
        width: temImg.width,
        height: temImg.height,
      });
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    temImg.onerror = function (err) {
      console.log("任务图片识别失败", err);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(DEFAULT_MESSAGES.TASK_IMAGE_RECOGNITION_FAILED);
    };
  });
}
